import {
	ReactElement,
	Ref,
	RefObject,
	forwardRef,
	useImperativeHandle,
	useMemo,
	useRef
} from "react";
import { GridItem as GridItemModel} from "./SharePointOverView";
import {
	MediaQueryKind,
	MultiSelectableOptions,
	SelectableItemOptions,
	useIsTouchDevice,
	useUserTimezone,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	SharePointDocumentAction,
	SharePointDocumentActionContext
} from "./actions";
import {
	computeContextMenuTitle,
	ContextMenu,
	DateString,
	GridView,
	Icon,
	LoadMoreIndicator,
	Space,
	PipeSeparator,
	useContextMenu
} from "client/Components";
import { TimeZone } from "client/DateAndTime/timezones";
import { DocumentGridViewItem } from "../DocumentGridViewItem";
import { GridViewGroupItem, GridViewItemKind } from "client/Components/GridView/groupGridViewItems";
import { localize } from "client_legacy/ndaw/culture";
import { isNotNil } from "client/Misc/utils";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { urlEncodeObject } from "client_legacy/ndaw/urlHelper";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import { createUrlRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import { DocumentGridViewContainer } from "../DocumentGridViewContainer";
import {
	DocumentItemViewModel,
	DocumentItemType,
	ListLocationViewModel
} from "client/Api/Documents/SharePoint/common";
import { DocumentNoResult } from "../DocumentNoResult";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type SharePointGridViewProps = {
	isBusy: boolean;
	hasMoreItems: boolean;
	multiSelection: MultiSelectableOptions;
	selectionFor: (item: GridItemModel) => SelectableItemOptions;
	items: readonly GridItemModel[];
	selectionActions: readonly SharePointDocumentAction[];
	actionContext: SharePointDocumentActionContext;
	isInSelectMode: boolean;
	onLoadMoreItems(): void;
	onOpenItem: (item: DocumentItemViewModel) => void;
	onEnableSelectMode(): void;
	hasSearch: boolean;
	loadMoreIndicatorVersion: number;
};

export const SharePointGridView = forwardRef(
	(props: SharePointGridViewProps, ref: Ref<SharePointGridViewHandle>): ReactElement => {
		const {
			isBusy,
			hasMoreItems,
			items,
			selectionActions,
			actionContext,
			multiSelection,
			isInSelectMode,
			onEnableSelectMode,
			onLoadMoreItems,
			selectionFor,
			onOpenItem,
			hasSearch,
			loadMoreIndicatorVersion
		} = props;

		const userTimezone: TimeZone = useUserTimezone();
		const gridRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
		const [contextMenuHandler, contextMenuProps] = useContextMenu(gridRef);
		const isTouch: boolean = useIsTouchDevice();
		const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

		const itemsToRender = useMemo(
			(): readonly GridViewGroupItem<GridItemModel>[] => {
				return items.map(
					x => ({
						model: x,
						uniqueKey: x.uniqueKey,
						itemKind: x.model.itemType === DocumentItemType.Folder
							? GridViewItemKind.Default
							: GridViewItemKind.Preview
					})
				);
			},
			[items]
		);

		useImperativeHandle(
			ref,
			() => ({
				resetScrollPosition: () => {
					gridRef.current
						?.scrollTo(0, 0);
				}
			}),
			[]
		);

		const { doesSupportThumbnails } = documentsConfiguration()
			.sharePoint;

		if (!isBusy && items.length === 0) {
			return (
				<DocumentNoResult
					hasSearch={hasSearch}
				/>
			);
		}

		return (
			<DocumentGridViewContainer>
				<GridView
					trackingId="share-point-grid-view"
					ref={gridRef}
					items={itemsToRender}
					elementToRenderAfterItems={
						<>
							<LoadMoreIndicator
								key={loadMoreIndicatorVersion}
								hasMoreItems={hasMoreItems}
								isLoadingData={isBusy}
								onRequestMoreItems={onLoadMoreItems}
							/>
							{items.length > 0 && (
								<AiAssistantFabSpacing />
							)}
						</>
					}
					multiSelection={multiSelection}
					hasLassoSelection={!isMobile}
				>
					{(item) => {
						const {
							itemKind,
							model,
							uniqueKey
						} = item;

						const shouldShowPreview: boolean = doesSupportThumbnails
							&& model.model.itemType !== DocumentItemType.Folder;

						const previewUrl: string | undefined = shouldShowPreview
							? getSharePointItemPreviewUrl(model.id, model.model.location)
							: undefined;

						return (
							<DocumentGridViewItem
								key={uniqueKey}
								id={model.id}
								name={model.name}
								additionalInfo={(
									<Info
										modifiedBy={model.modifiedBy}
										modifiedOn={model.modifiedOn}
										size={model.size}
										timezone={userTimezone}
									/>
								)}
								isBookmarked={Boolean(model.model.bookmarkId)}
								isInfected={model.isInfected ?? false}
								previewUrl={previewUrl}
								selectable={selectionFor(model)}
								documentKind={model.model
									.itemType}
								isMobile={isMobile}
								selectionActions={selectionActions}
								actionContext={{...actionContext, selection: [model.model]}}
								isTouch={isTouch}
								isPreview={itemKind === GridViewItemKind.Preview}
								isInSelectMode={isInSelectMode}
								onContextMenu={contextMenuHandler}
								onOpenItem={() => onOpenItem(model.model)}
								onEnableSelectMode={onEnableSelectMode}
							/>
						);
					}
				}
				</GridView>
				{contextMenuProps && (
					<ContextMenu
						{...contextMenuProps}
						actions={selectionActions}
						context={actionContext}
						trackingId="sharepoint-context-menu"
						title={computeContextMenuTitle(
							actionContext.selection,
							(x) => x.name
						)}
					/>
				)}
			</DocumentGridViewContainer>
		);
	}
);

SharePointGridView.displayName = "SharePointGridView";

type InfoProps = {
	modifiedOn: Date;
	size: number | nil;
	timezone: TimeZone;
	modifiedBy: string | nil;
};

export const Info = (props: InfoProps): ReactElement => {
	const {
		modifiedOn,
		size,
		timezone,
		modifiedBy
	} = props;

	return (
		<>
			<Icon value="pen" />
			<Space />
			<DateString
				format="RelativeTimestamp"
				timezone={timezone}
				value={modifiedOn}
			/>
			{modifiedBy && (
				<>
					<Space />
					{localize("_(Edited)By")}
					<Space />
					{modifiedBy}
				</>
			)}
			{isNotNil(size) && (
				<>
					<PipeSeparator />
					{getDisplayFriendlySize(size)}
				</>
			)}
		</>
	);
};

Info.displayName = "Info";

export function getSharePointItemPreviewUrl(
	id: number,
	location: ListLocationViewModel
): string {
	const queryString: string = urlEncodeObject({
		location: convertToLocationModel(location),
		itemId: id
	});

	return createUrlRelativeToEnvironment(`Documents/SharePoint/Thumbnail?${queryString}`);
}

export type SharePointGridViewHandle = {
	resetScrollPosition: () => void;
};